import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, FormHelperText } from '@mui/material';

import { RecordValueItem } from './RecordValueItem';
import { RecordModal } from '../modals/RecordModal';
import { SelectedRecordChip } from './SelectedRecordChip';
import { RecordsItemWrapper, RecordValueWrapper } from './RecordsItem.styles';
import type { FormItemUiDefinition } from 'types/formItem.ts';
import { SearchableBrowseItem } from 'components/common/SearchableBrowseItem/SearchableBrowseItem';
import { SelectedRowsWrapper } from 'components/common/SearchableBrowseItem/styledComponents';
import { EMPTY_DISPLAY_LABEL } from '../utils/selectedRows';
import { useRecordsItem } from '../utils/useRecordsItem';

export interface RecordsItemProps {
  moduleId?: string;
  parentAgendaId?: string;
  recordId?: string;
  systemType?: string;
  asColumns?: boolean;
  lightError?: boolean;
  onConfirm?: () => void;
  formItemUiDefinition?: FormItemUiDefinition | null;
  formOptionShouldDirty?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export const RecordsItem = memo(
  ({
    moduleId = '',
    systemType = '',
    asColumns = false,
    onConfirm,
    formItemUiDefinition,
    parentAgendaId,
    recordId: parentRecordId,
    formOptionShouldDirty,
    ...formItem
  }: RecordsItemProps) => {
    const { t } = useTranslation();

    const {
      isMultiSelect,
      isEditable,
      selectedRows,
      displayValues,
      chipRows,
      modalState,
      validateOnBlur,
      handleOnClick,
      handleSearchSubmit,
      handleClear,
      handleAutoSelectConsumed,
      handleCloseModal,
      handleModalSubmit,
      formItemIdToShow,
    } = useRecordsItem({ formItem, moduleId, systemType, onConfirm, formOptionShouldDirty });

    const renderField = useCallback(() => {
      if (!isMultiSelect) {
        const selectedValue = String(displayValues[0]?.value ?? '');
        const selectedUiDefinition = displayValues[0]?.formItemUiDefinition ?? formItemUiDefinition;

        if (isEditable) {
          return (
            <SearchableBrowseItem
              name={selectedValue}
              onSearchSubmit={handleSearchSubmit}
              isSingleBrowse={true}
              error={Boolean(formItem.error)}
              lightError={Boolean(formItem.lightError)}
              formItemUiDefinition={selectedUiDefinition}
              onClear={handleClear}
              onBlur={validateOnBlur}
            />
          );
        }

        if (!displayValues.length) {
          return null;
        }

        return displayValues.map((item) => (
          <RecordValueItem
            key={item.id || item.value}
            value={item.value || EMPTY_DISPLAY_LABEL}
            cursorPointer={true}
            onClick={() => handleOnClick(item.id || undefined)}
            formItemUiDefinition={item.formItemUiDefinition ?? formItemUiDefinition}
          />
        ));
      }

      return isEditable ? (
        <SearchableBrowseItem
          name=""
          onSearchSubmit={handleSearchSubmit}
          isSingleBrowse={false}
          error={Boolean(formItem.error)}
          lightError={Boolean(formItem.lightError)}
          onBlur={validateOnBlur}
        />
      ) : null;
    }, [
      displayValues,
      formItem.error,
      formItem.lightError,
      formItemUiDefinition,
      handleClear,
      handleOnClick,
      handleSearchSubmit,
      validateOnBlur,
      isEditable,
      isMultiSelect,
    ]);

    return (
      <>
        <RecordsItemWrapper>
          <RecordValueWrapper asColumns={asColumns}>
            {chipRows.length > 0 && (
              <SelectedRowsWrapper>
                {chipRows.map((item) => (
                  <SelectedRecordChip key={item.id || item.value} item={item} onClick={handleOnClick} />
                ))}
              </SelectedRowsWrapper>
            )}

            <Box sx={{ flex: 1, minWidth: 0 }}>{renderField()}</Box>
          </RecordValueWrapper>
          {formItem.error && !formItem.lightError && (
            <FormHelperText error variant="filled">
              {formItem.error.message}
            </FormHelperText>
          )}
        </RecordsItemWrapper>
        {modalState.open && (
          <RecordModal
            open={true}
            {...formItem.definition.records}
            title={t('flow.modulesAndAgendas.form.item.records._')}
            formItemIdToShow={formItemIdToShow}
            selectedRows={selectedRows}
            parentAgendaId={parentAgendaId}
            recordId={parentRecordId}
            initialSearchTerm={modalState.searchTerm}
            isAutoSelectSearchResultEnabled={modalState.isAutoSelectSearchResultEnabled}
            onAutoSelectConsumed={handleAutoSelectConsumed}
            onSubmit={handleModalSubmit}
            onClose={handleCloseModal}
          />
        )}
      </>
    );
  }
);
